import { Routes } from '@angular/router';
import { advanceMaintenanceModalityGuard } from '@features/configuration/guards/advance-maintenance-modality.guard';
import { advancedMaintenanceEnabledGuard } from '@features/configuration/guards/advanced-maintenance-enabled.guard';
import { manageTraceabilityEnabledGuard } from '@features/configuration/guards/manage-traceability-enabled.guard';

export const ReportsRoutes: Routes = [
  {
    path: 'reports',
    title: 'Reportes',
    data: { label: 'Reportes', icon: 'report', permission: 'reports.view' },
    children: [
      {
        path: '',
        loadComponent: () => import('@features/reports/reports.component'),
      },
      // Vehiculos
      {
        path: 'vehicles',
        title: 'Vehículos',
        data: {
          icon: 'directions_car',
          color: 'primary',
          description:
            'Consulta reportes relacionados con el estado, mantenimiento y vencimientos de la flota vehicular.',
          backFallback: '/reports',
        },
        loadComponent: () =>
          import('@features/reports/views/reports-vehicles/reports-vehicles.component'),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'measurement-history',
          },
          {
            path: 'measurement-history',
            title: 'Historial de mediciones',
            data: { label: 'Historial de mediciones', icon: 'history', backFallback: '/reports' },
            loadComponent: () =>
              import('@features/reports/views/reports-vehicles/report-measurement-history/report-measurement-history.component'),
          },
          {
            path: 'vehicle-expirations',
            title: 'Reporte de vencimientos de vehículos',
            data: {
              label: 'Reporte de vencimientos de vehículos',
              icon: 'history',
              backFallback: '/reports',
            },
            loadComponent: () =>
              import('@features/reports/views/reports-vehicles/report-vehicle-expiration/report-vehicle-expiration.component'),
          },
          {
            path: 'traceability-expirations',
            title: 'Trazabilidad de vencimientos',
            canActivate: [manageTraceabilityEnabledGuard],
            data: {
              label: 'Trazabilidad de vencimientos',
              icon: 'history',
              backFallback: '/reports',
              redirectWhenDisabled: '/reports/vehicles/measurement-history',
            },
            loadComponent: () =>
              import('@features/reports/views/reports-vehicles/report-traceability-expiration/report-traceability-expiration.component'),
          },
          {
            path: 'daily-vehicle-status',
            title: 'Estado diario de vehículos',
            data: {
              label: 'Estado diario de vehículos',
              icon: 'history',
              backFallback: '/reports',
            },
            loadComponent: () =>
              import('@features/reports/views/reports-vehicles/report-daily-vehicle-status/report-daily-vehicle-status.component'),
          },
          {
            path: 'vehicle-kilometers',
            title: 'Mediciones de placa por periodo',
            data: {
              label: 'Mediciones de placa por periodo',
              icon: 'history',
              backFallback: '/reports',
            },
            loadComponent: () =>
              import('@features/reports/views/reports-vehicles/report-vehicle-kilometer/report-vehicle-kilometer.component'),
          },
          {
            path: 'basic-maintenance',
            title: 'Reporte de mantenimiento basico',
            canActivate: [advanceMaintenanceModalityGuard],
            data: {
              label: 'Reporte de mantenimiento basico',
              icon: 'history',
              backFallback: '/reports',
            },
            loadComponent: () =>
              import('@features/reports/views/reports-vehicles/report-basic-maintenance/report-basic-maintenance.component'),
          },
        ],
      },
      // Conductores
      {
        path: 'drivers',
        title: 'Conductores',
        data: {
          icon: 'id_card',
          color: 'primary-light',
          description: 'Conductores y vencimientos de sus licencias.'
        },
        loadComponent: () =>
          import('@features/reports/views/reports-drivers/reports-drivers.component'),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'driver-expirations',
          },
          {
            path: 'driver-expirations',
            title: 'Reporte de vencimientos de licencias',
            data: {
              label: 'Reporte de vencimientos de licencias',
              icon: 'history',
              backFallback: '/reports',
            },
            loadComponent: () =>
              import('@features/reports/views/reports-drivers/report-drivers-expiration/report-drivers-expiration.component'),
          },
        ],
      },
      // Listas de chequeo
      {
        path: 'checklists',
        title: 'Listas de chequeo',
        data: {
          icon: 'fact_check',
          color: 'accent',
          description: 'Listas de chequeo de vehículos y conductores.',
          backFallback: '/reports',
        },
        loadComponent: () =>
          import('@features/reports/views/reports-checklists/reports-checklists.component'),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'alerts',
          },
          {
            path: 'alerts',
            title: 'Alertas de listas de chequeo',
            data: {
              label: 'Alertas de listas de chequeo',
              icon: 'history',
              backFallback: '/reports',
            },
            loadComponent: () =>
              import('@features/reports/views/reports-checklists/report-alert-checklist/report-alert-checklist.component'),
          },
          {
            path: ':alertId/files',
            title: 'Archivos asociados a la alerta',
            data: { backFallback: '/reports/checklists/alerts' },
            loadComponent: () =>
              import(
                '@features/reports/views/reports-checklists/report-alert-checklist/view/alert-files/alert-files.component'
              ).then((m) => m.AlertFilesComponent),
          },
          {
            path: 'results-checklists/:inspectionId/detail',
            title: 'Detalle de lista de chequeo',
            data: { backFallback: '/reports/checklists/results-checklists' },
            loadComponent: () =>
              import(
                '@features/reports/views/reports-checklists/report-results-checklist/view/report-results-checklist-detail/report-results-checklist-detail.component'
              ).then((m) => m.ReportResultsChecklistDetailComponent),
          },
          {
            path: 'results-checklists/:inspectionId/signatures',
            title: 'Firmas de lista de chequeo',
            data: { backFallback: '/reports/checklists/results-checklists' },
            loadComponent: () =>
              import(
                '@features/reports/views/reports-checklists/report-results-checklist/view/report-results-checklist-signatures/report-results-checklist-signatures.component'
              ).then((m) => m.ReportResultsChecklistSignaturesComponent),
          },
          {
            path: 'results-checklists/:inspectionId/compliance',
            title: 'Reporte de cumplimiento',
            data: { backFallback: '/reports/checklists/results-checklists' },
            loadComponent: () =>
              import(
                '@features/reports/views/reports-checklists/report-results-checklist/view/report-results-checklist-compliance/report-results-checklist-compliance.component'
              ).then((m) => m.ReportResultsChecklistComplianceComponent),
          },
          {
            path: 'results-checklists',
            title: 'Resultados de listas de chequeo',
            data: {
              label: 'Resultados de listas de chequeo',
              icon: 'history',
              backFallback: '/reports',
            },
            loadComponent: () =>
              import('@features/reports/views/reports-checklists/report-results-checklist/report-results-checklist.component'),
          },
          {
            path: 'frecuency',
            title: 'Periodicidad de listas de chequeo',
            data: {
              label: 'Informe Periodicidad de lista de chequeo por placa',
              icon: 'history',
              backFallback: '/reports',
            },
            loadComponent: () =>
              import('@features/reports/views/reports-checklists/report-frecuency-checklist/report-frecuency-checklist.component'),
          },
        ],
      },
      // Incidentes
      {
        path: 'incidents',
        title: 'Incidentes',
        data: {
          icon: 'warning',
          color: 'warning',
          description: 'Incidentes de vehículos y conductores.',
          backFallback: '/reports',
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('@features/reports/views/reports-incident/reports-incident.component'),
          },
          {
            path: ':incidentId/files',
            title: 'Archivos asociados al incidente',
            data: { backFallback: '/reports/incidents' },
            loadComponent: () =>
              import(
                '@features/reports/views/reports-incident/view/incident-files/incident-files.component'
              ).then((m) => m.IncidentFilesComponent),
          },
        ],
      },
      // Combustible
      {
        path: 'fuel',
        title: 'Combustible',
        data: {
          icon: 'local_gas_station',
          color: 'secondary',
          description: 'Combustible de vehículos.'
        },
        loadComponent: () => import('@features/reports/views/reports-fuel/reports-fuel.component'),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'performance-history-vehicle',
          },
          {
            path: 'performance-history-vehicle',
            title: 'Historial de rendimiento por vehículo',
            data: {
              label: 'Historial de rendimiento por vehículo',
              icon: 'history',
              backFallback: '/reports',
            },
            loadComponent: () =>
              import('@features/reports/views/reports-fuel/report-performance-history-vehicle/report-performance-history-vehicle.component'),
          },
        ],
      },
      // Mantenimiento avanzado
      {
        path: 'advanced-maintenance',
        title: 'Mantenimiento avanzado',
        canActivate: [advancedMaintenanceEnabledGuard],
        data: {
          icon: 'build',
          color: 'success',
          description: 'Consulta de trabajos y repuestos.',
          headerTitle: 'Consulta de trabajos y repuestos',
          backFallback: '/reports',
          redirectWhenDisabled: '/reports',
        },
        loadComponent: () =>
          import('@features/reports/views/reports-advanced-maintenance/reports-advanced-maintenance.component'),
      },
    ],
  },
];
